import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ShieldCheck } from "@phosphor-icons/react";
import seoRoutes from "@/data/seo-routes.json";

const SECTIONS = [
  {
    id: "intro",
    title: "1. Introduction",
    body: (
      <>
        <p>
          TechConnect LLC ("we", "us", or "our") respects your privacy. This Privacy Policy explains
          what information we collect when you visit{" "}
          <a className="text-blue-600 hover:underline" href="https://techconnectncllc.services">
            techconnectncllc.services
          </a>{" "}
          or use our IT services, how we use it, who we share it with, and the choices you have.
        </p>
        <p>
          By using our website or services, you agree to the practices described here. If you do not
          agree, please do not use our website.
        </p>
      </>
    ),
  },
  {
    id: "info",
    title: "2. Information We Collect",
    body: (
      <>
        <p className="font-semibold text-zinc-900">Information you provide:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Name, email address, and phone number submitted through our contact or booking forms</li>
          <li>Service of interest, requested appointment date/time, and any notes you include</li>
          <li>Payment information (handled by Stripe — we never receive or store full card numbers)</li>
        </ul>
        <p className="font-semibold text-zinc-900 mt-4">Information collected automatically:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>IP address, browser type, device type, operating system, referring URL</li>
          <li>Pages viewed, links clicked, time spent, and approximate geographic region</li>
          <li>Cookies and similar technologies (see Section 5)</li>
        </ul>
      </>
    ),
  },
  {
    id: "use",
    title: "3. How We Use Your Information",
    body: (
      <ul className="list-disc pl-6 space-y-1">
        <li>To respond to your inquiries, deliver requested services, and schedule appointments</li>
        <li>To process payments and send invoices/receipts</li>
        <li>To send appointment reminders or follow-ups about your service</li>
        <li>To improve our website, services, and customer experience</li>
        <li>To detect, prevent, and respond to fraud or security threats</li>
        <li>To comply with legal obligations</li>
      </ul>
    ),
  },
  {
    id: "share",
    title: "4. Service Providers We Share Data With",
    body: (
      <>
        <p>
          We do not sell your personal information. We share it only with vendors that help us run
          our business, under contracts that require them to safeguard your data:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Pipedrive</strong> — customer relationship management (your name, email, phone, service interest)</li>
          <li><strong>Stripe</strong> — secure payment processing (PCI-compliant; we never see card numbers)</li>
          <li><strong>Google (Workspace, Ads, Tag Manager)</strong> — email delivery, ad performance, and site analytics</li>
          <li><strong>Meta (Facebook) Pixel</strong> — ad performance measurement for our Facebook/Instagram ads</li>
          <li><strong>MongoDB Atlas / hosting providers</strong> — secure data storage of your booking and contact records</li>
        </ul>
        <p>
          We may also disclose information when required by law, to enforce our agreements, or to
          protect the rights, safety, or property of TechConnect LLC, our customers, or others.
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    title: "5. Cookies & Tracking Technologies",
    body: (
      <>
        <p>
          We use cookies and similar technologies (pixels, local storage, gtag) to remember
          preferences, measure site performance, and serve more relevant ads. Specifically:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Google Analytics &amp; Google Ads (gtag.js, GTM)</strong> — measure traffic and ad conversions</li>
          <li><strong>Meta Pixel</strong> — measure Facebook/Instagram ad performance</li>
          <li><strong>Stripe</strong> — fraud detection during checkout</li>
        </ul>
        <p>
          You can disable cookies in your browser settings. Disabling them may affect some site
          features (such as remembering you on return visits).
        </p>
      </>
    ),
  },
  {
    id: "retention",
    title: "6. How Long We Keep Your Data",
    body: (
      <p>
        We retain personal information only as long as needed for the purposes described in this
        policy or as required by law. Lead and booking records are typically kept for up to 7 years
        for tax, accounting, and service-history purposes. You can request earlier deletion at any
        time (see Section 8).
      </p>
    ),
  },
  {
    id: "security",
    title: "7. Security",
    body: (
      <p>
        We use industry-standard safeguards — encrypted connections (HTTPS), access controls, and
        reputable third-party platforms — to protect your data. No system is 100% secure, however,
        and we cannot guarantee absolute security of information transmitted over the internet.
      </p>
    ),
  },
  {
    id: "rights",
    title: "8. Your Rights & Choices",
    body: (
      <>
        <p>
          Depending on where you live (e.g., California, EU/UK), you may have the right to:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Access the personal information we have about you</li>
          <li>Correct inaccurate information</li>
          <li>Delete your information ("right to be forgotten")</li>
          <li>Opt out of certain processing, including targeted advertising</li>
          <li>Receive a portable copy of your data</li>
        </ul>
        <p>
          To exercise any of these rights, email us at{" "}
          <a className="text-blue-600 hover:underline" href="mailto:techconnectncllc@gmail.com">
            techconnectncllc@gmail.com
          </a>{" "}
          with the subject line "Privacy Request". We respond within 30 days.
        </p>
        <p>
          You can also opt out of Google Ads personalization at{" "}
          <a className="text-blue-600 hover:underline" href="https://adssettings.google.com" target="_blank" rel="noreferrer">
            adssettings.google.com
          </a>{" "}
          and Meta ads at{" "}
          <a className="text-blue-600 hover:underline" href="https://www.facebook.com/adpreferences/" target="_blank" rel="noreferrer">
            facebook.com/adpreferences
          </a>.
        </p>
      </>
    ),
  },
  {
    id: "children",
    title: "9. Children's Privacy",
    body: (
      <p>
        Our services are not directed to children under 13. We do not knowingly collect personal
        information from anyone under 13. If you believe we have inadvertently collected such
        information, please contact us and we will delete it.
      </p>
    ),
  },
  {
    id: "changes",
    title: "10. Changes to This Policy",
    body: (
      <p>
        We may update this Privacy Policy from time to time. When we do, we will revise the "Last
        updated" date at the top. Material changes will be highlighted on our home page or sent to
        you by email when reasonable.
      </p>
    ),
  },
  {
    id: "contact",
    title: "11. Contact Us",
    body: (
      <>
        <p>
          Questions, requests, or complaints about this policy or your data:
        </p>
        <ul className="list-none space-y-1">
          <li><strong>TechConnect LLC</strong></li>
          <li>Offices in Clayton, NC and Granite Falls, NC</li>
          <li>Phone: <a className="text-blue-600 hover:underline" href="tel:+19197375442">(919) 737-5442</a></li>
          <li>Email: <a className="text-blue-600 hover:underline" href="mailto:techconnectncllc@gmail.com">techconnectncllc@gmail.com</a></li>
        </ul>
      </>
    ),
  },
];

export default function Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="privacy-page" className="min-h-screen bg-white">
      <Helmet>
        <title>{seoRoutes["/privacy"].title}</title>
        <meta name="description" content={seoRoutes["/privacy"].description} />
        <link rel="canonical" href="https://techconnectncllc.services/privacy" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      {/* Hero */}
      <header className="bg-zinc-950 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20">
          <Link
            to="/"
            data-testid="privacy-back-home"
            className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white"
          >
            <ArrowLeft size={16} weight="bold" /> Back to home
          </Link>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs uppercase tracking-widest text-blue-300">
            <ShieldCheck size={14} weight="fill" /> Privacy Policy
          </div>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter">
            Your data, our promise.
          </h1>
          <p className="mt-3 text-zinc-300">
            Last updated: December 1, 2025 · Effective: December 1, 2025
          </p>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-[200px_1fr] gap-10">
        {/* TOC */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Contents</div>
            <ul className="space-y-2 text-sm">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="text-zinc-600 hover:text-blue-600 transition-colors">
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <article className="prose prose-zinc max-w-none">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24 mb-10">
              <h2 className="font-heading text-2xl font-bold text-zinc-900 mb-4">{s.title}</h2>
              <div className="space-y-4 text-zinc-700 leading-relaxed">{s.body}</div>
            </section>
          ))}
        </article>
      </main>

      <footer className="border-t border-zinc-200 bg-zinc-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-zinc-500">
          <div>© {new Date().getFullYear()} TechConnect LLC. All rights reserved.</div>
          <Link to="/" className="text-blue-600 hover:underline" data-testid="privacy-footer-home">
            techconnectncllc.services
          </Link>
        </div>
      </footer>
    </div>
  );
}
